import { useEffect, useLayoutEffect, useRef } from 'react'
import type { ChangeEvent, KeyboardEvent } from 'react'
import { Send, Square } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CHAT_MESSAGE_MAX_LENGTH } from '@/lib/chatTypes'

// ~8 lignes de 24px + padding vertical.
const MAX_TEXTAREA_HEIGHT_PX = 208
// Le compteur n'apparaît qu'à l'approche de la limite.
const COUNTER_THRESHOLD = 500

export type ChatInputProps = {
  value: string
  onValueChange: (value: string) => void
  onSend: (text: string) => void
  onStop?: () => void
  isStreaming?: boolean
  disabled?: boolean
  placeholder?: string
  className?: string
}

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2'

// Textarea NATIVE (le `Textarea` shadcn n'a pas de forwardRef → ref avalée).
export function ChatInput({
  value,
  onValueChange,
  onSend,
  onStop,
  isStreaming = false,
  disabled = false,
  placeholder = 'Écrivez votre message…',
  className,
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const refocusRef = useRef(false)
  const isDisabled = disabled || isStreaming
  const canSend = !isDisabled && value.trim().length > 0 && value.length <= CHAT_MESSAGE_MAX_LENGTH
  const remaining = CHAT_MESSAGE_MAX_LENGTH - value.length

  // Auto-resize : seul style inline autorisé (hauteur calculée).
  useLayoutEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT_PX)}px`
    el.style.overflowY = el.scrollHeight > MAX_TEXTAREA_HEIGHT_PX ? 'auto' : 'hidden'
  }, [value])

  // Le textarea est désactivé pendant le stream (perte de focus) : on rend le
  // focus à la fin de la réponse si l'utilisateur venait d'envoyer.
  useEffect(() => {
    if (!isDisabled && refocusRef.current) {
      refocusRef.current = false
      textareaRef.current?.focus()
    }
  }, [isDisabled])

  const submit = () => {
    if (!canSend) return
    refocusRef.current = true
    onSend(value.trim())
    textareaRef.current?.focus()
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter' || e.shiftKey) return
    if (e.nativeEvent.isComposing) return
    e.preventDefault()
    submit()
  }

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onValueChange(e.target.value.slice(0, CHAT_MESSAGE_MAX_LENGTH))
  }

  return (
    <div className={cn('space-y-1', className)}>
      <div className="flex items-end gap-2 rounded-xl border border-slate-200 bg-white p-2 shadow-sm focus-within:border-violet-300">
        <textarea
          ref={textareaRef}
          rows={1}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={isDisabled}
          maxLength={CHAT_MESSAGE_MAX_LENGTH}
          placeholder={placeholder}
          aria-label="Envoyer un message à l'agent"
          className={cn(
            'flex-1 resize-none bg-transparent px-2 py-1.5 text-sm leading-6 text-slate-900 placeholder:text-muted-foreground outline-none disabled:cursor-not-allowed disabled:opacity-60',
            // AC 10 : focus clairement visible (ring-2 ring-offset-2, comme les
            // boutons). `:focus-visible` matche toujours un champ texte focalisé.
            'rounded-md',
            FOCUS_RING
          )}
        />
        {isStreaming && onStop ? (
          <button
            type="button"
            onClick={onStop}
            aria-label="Arrêter la réponse de l'agent"
            className={cn(
              'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50',
              FOCUS_RING
            )}
          >
            <Square aria-hidden="true" className="h-3.5 w-3.5" />
            Stop
          </button>
        ) : (
          <button
            type="button"
            onClick={submit}
            disabled={!canSend}
            aria-label="Envoyer"
            className={cn(
              'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-r from-rose-500 to-violet-500 text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40',
              FOCUS_RING
            )}
          >
            <Send aria-hidden="true" className="h-4 w-4" />
          </button>
        )}
      </div>
      {remaining <= COUNTER_THRESHOLD ? (
        <p className="text-right text-xs text-slate-500" aria-live="polite">
          {remaining} caractère{remaining > 1 ? 's' : ''} restant{remaining > 1 ? 's' : ''}
        </p>
      ) : null}
    </div>
  )
}
